import { isEqual, reduce } from 'lodash';

import {
  EDITOR_RESOLVERS,
  TEMPLATE_DELETION_STATUSES,
  TEMPLATE_TYPES,
  VIEW_MODE_OPTIONS,
  PDF_FORMAT_OPTIONS,
  TEMPLATE_TYPE_OPTIONS,
  PDF_ORIENTATION_OPTIONS,
} from '@common/constants';

const cheerio = require('cheerio');

const mmToIn = (mm) => mm / 25.4;
const mmToPx = (mm) => (mm / 25.4) * 96;
const mmToPt = (mm) => (mm / 25.4) * 72;

function updateFavicon(iconUrl) {
  const link = document.querySelector("link[rel*='icon']");

  if (link) {
    link.href = iconUrl;
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.href = iconUrl;
    document.head.appendChild(newLink);
  }
}

function removeHandlebarsEach(html) {
  const $ = cheerio.load(html, { decodeEntities: false });

  $('*')
    .contents()
    .filter((_, el) => el.type === 'text')
    .each((_, el) => {
      const cleaned = el.data
        .replace(/{{#each\s+[^}]+}}/g, '')
        .replace(/{{\/each}}/g, '');

      if (cleaned.trim() === '') {
        $(el).remove();
      } else {
        el.data = cleaned;
      }
    });

  return $.html();
}

function parseEachOnTbodyRecursive(html) {
  const $ = cheerio.load(html, { xmlMode: false });

  function processNext() {
    const el = $('tbody[data-hbs-each]').first();
    if (!el.length) return false;

    const eachValue = el.attr('data-hbs-each');
    el.removeAttr('data-hbs-each');

    const innerHtml = el.html();
    el.html(`{{#each ${eachValue}}}\n${innerHtml}\n{{/each}}`);

    return true;
  }

  function processCardsNext() {
    const el = $('div[data-hbs-cards-each]').first();
    if (!el.length) return false;

    const eachValue = el.attr('data-hbs-cards-each');
    el.removeAttr('data-hbs-cards-each');

    const outerHtml = $.html(el);
    const wrapped = `{{#each ${eachValue}}}\n${outerHtml}\n{{/each}}`;
    el.replaceWith(wrapped);

    return true;
  }

  while (processNext()) {}
  while (processCardsNext()) {}

  const page = $('div.page').first();

  if (page?.length) {
    let style = page.attr('style') || '';

    style = style
      .replace(/overflow\s*:\s*[^;]+;?/gi, 'overflow-h: hidden;')
      .trim();

    style = style.replace(/\s{2,}/g, ' ').trim();

    if (style === '') {
      page.removeAttr('style');
    } else {
      page.attr('style', style);
    }
  }

  return $.html();
}

const filterEmptyStrings = (filterObject) => {
  if (!filterObject) return;
  let checkedObj = { ...filterObject };
  // eslint-disable-next-line array-callback-return
  Object.keys(checkedObj).map((column) => {
    if (
      checkedObj[column] === undefined ||
      checkedObj[column] === null ||
      checkedObj[column] === ''
    )
      delete checkedObj[column];
  });
  return checkedObj;
};

const getLocalStorage = (key) => {
  let data = window.localStorage.getItem('store') || null;
  data = JSON.parse(data);
  if (!data) return null;
  return key in data ? data[key] : null;
};

const setLocalStorage = (key, value) => {
  let data = window.localStorage.getItem('store') || null;
  data = JSON.parse(data) || {};
  data[key] = value;
  window.localStorage.setItem('store', JSON.stringify(data));
};

const getSessionStorage = (key) => {
  let data = window.sessionStorage.getItem('store') || null;
  data = JSON.parse(data);
  if (!data) return null;
  return key in data ? data[key] : null;
};

const setSessionStorage = (key, value) => {
  let data = window.sessionStorage.getItem('store') || null;
  data = JSON.parse(data) || {};
  data[key] = value;
  window.sessionStorage.setItem('store', JSON.stringify(data));
};

const clearLocalStorage = (key) => {
  window.localStorage.removeItem(key);
};

const copyObjectWithoutRef = (providedObj) =>
  JSON.parse(JSON.stringify(providedObj));

const findStringIndex = (array = [], target) => {
  return array.indexOf(target);
};

const parseWidth = (width) => {
  if (!width) return 0;
  return parseFloat(width.replace('%', ''));
};

const calcNodesWidth = (childNodes = [], allNodes = []) => {
  return childNodes.reduce((sum, childId) => {
    const childNode = allNodes[childId];
    const childProps = childNode?.data?.props || {};
    return sum + parseWidth(childProps.width || childProps.style?.width);
  }, 0);
};

const checkChangedFields = (initialObject, comparisonObject) => {
  let changedFieldsKeys = reduce(
    initialObject,
    function (result, value, key) {
      return isEqual(value, comparisonObject?.[key])
        ? result
        : result.concat(key);
    },
    [],
  );

  return changedFieldsKeys.length ? true : false;
};

const getTemplateSource2 = ({ structure, ...rest }) => {
  if (structure) {
    try {
      return {
        structure: JSON.parse(structure),
        ...rest,
      };
    } catch (err) {
      console.log('err: ', err);
      return {};
    }
  }
  console.log('err: structure is missing');
  return {};
};

const getTemplateSource = (templateDetails) => {
  if (templateDetails?.templateSource?.structure) {
    try {
      return {
        parsedStructure: JSON.parse(templateDetails.templateSource.structure),
      };
    } catch (err) {
      console.log('err: ', err);
      return { parseError: 'Error occurred while source been parsed.' };
    }
  }
  return {
    parseError: 'Error occurred while source been parsed: Missing source.',
  };
};

const readDownload = (downloadData) => {
  try {
    if (downloadData?.data) {
      const objectUrl = window.URL.createObjectURL(
        new Blob([downloadData.data], {
          type: downloadData.headers['content-type'],
        }),
      );

      return objectUrl;
    }
  } catch (e) {}
};

const removeEditableFlags = (rawHTML) => {
  return rawHTML
    .replace(/\scontenteditable="true"/g, '')
    .replace(/\sdraggable="true"/g, '');
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email) => emailRegex.test(email);

const isResponseOk = (response) => {
  return response?.status === 200;
};

const isCreateOk = (response) => {
  return response?.status === 201;
};

const isResponseBarRequest = (response) => {
  return response?.status === 400;
};

const isNodeEmailContainerRow = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.EMAIL.CONTAINER_ROW;
};

const isNodeEmailContainerColumn = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.EMAIL.CONTAINER_COLUMN;
};

const isNodeTable = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.COMMON.TABLE;
};

const isNodeTableRow = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.COMMON.TABLE_ROW;
};

const isNodeTableCell = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.COMMON.TABLE_CELL;
};

const isNodeCards = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.PDF.CARDS;
};

const isNodeValid = (node) => {
  return node.data.props?.isValid;
};

const isNodePlaceholder = (node) => {
  return node?.data?.name === EDITOR_RESOLVERS.COMMON.PLACEHOLDER;
};

const findNodeByParentElement = (providedNode) => (node) =>
  node.id === providedNode?.data?.parent;

const findNodeById = (id) => (node) => node.id === id;

const getNodeSrc = (node) => {
  if (isNodeCards(node)) {
    return node?.data?.props?.cardSrc;
  }

  if (isNodeTable(node)) {
    return node?.data?.props?.listSrc;
  }

  return;
};

const findTableElementByParentId = (allNodes = [], parentId) => {
  const parentElement = allNodes.find(findNodeById(parentId));
  if (isNodeTableCell(parentElement)) {
    const tableRowEl = allNodes.find(findNodeByParentElement(parentElement));

    if (isNodeTableRow(tableRowEl)) {
      return allNodes.find(findNodeByParentElement(tableRowEl));
    }
  }

  return;
};

const findPathRecursive = (allNodes, parent, path) => {
  const tableEl = findTableElementByParentId(allNodes, parent);
  const parentListSrc = getNodeSrc(tableEl);

  if (tableEl) {
    path = `${parentListSrc}[0]${path}`;
    return findPathRecursive(allNodes, tableEl?.data?.parent, path);
  }
  return path;
};

const normalizeUrl = (url) => {
  if (!url) return '';
  // If it already starts with http:// or https:// or mailto: etc, keep it as is
  if (/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return url;
  }
  return `https://${url}`;
};

const getListOfNodes = (query) => {
  const allNodes = query?.getNodes();
  return Object?.values(allNodes);
};

const filterNodesByParentId = (parentId) => (node) =>
  node.data.parent === parentId;

const getParentSrc = ({ query, parentId }) => {
  const parentNode = query.node(parentId).get();
  if (isNodeCards(parentNode)) {
    return { parentSource: getNodeSrc(parentNode), parentListSrc: '' };
  } else {
    const allNodes = getListOfNodes(query);
    const tableElement = findTableElementByParentId(allNodes, parentId);

    return {
      parentSource: getNodeSrc(tableElement),
      parentListSrc: findPathRecursive(
        allNodes,
        tableElement?.data?.parent,
        '',
      ),
    };
  }
};

const urlRegex = /^(https?:\/\/)?([\w.-]+)\.([a-z\.]{2,6})([\/\w .-]*)*\/?$/i;

const isObject = (a) => a != null && a.constructor === Object;
const isString = (a) => typeof a === 'string';

const createHtmlStringWithConvertedDom = (htmlDataConverted) => {
  return (
    `<!DOCTYPE html><html><head><meta charset="UTF-8" /><title>{{subject}}</title></head><body>` +
    htmlDataConverted +
    `</body></html>`
  );
};

const isDetails = (viewMode) => {
  return viewMode === VIEW_MODE_OPTIONS.DETAILS;
};

const isEdit = (viewMode) => {
  return viewMode === VIEW_MODE_OPTIONS.EDIT;
};

const isCreate = (viewMode) => {
  return viewMode === VIEW_MODE_OPTIONS.isCreate;
};

const isTemplateTypeEmail = (templateType) => {
  return templateType === TEMPLATE_TYPES.EMAIL;
};

const isTemplateTypePdfStyled = (templateType) => {
  return templateType === TEMPLATE_TYPES.PDF_STYLED;
};

const isTemplateTypePdfProgrammatic = (templateType) => {
  return templateType === TEMPLATE_TYPES.PDF_PROGRAMMATIC;
};

const isTemplatePdf = (templateType) => {
  return (
    isTemplateTypePdfStyled(templateType) ||
    isTemplateTypePdfProgrammatic(templateType)
  );
};

const formatHeadersSize = (sizes) => {
  return {
    min: {
      header: mmToPx(sizes.min.headerMm),
      footer: mmToPx(sizes.min.footerMm),
    },
    standard: {
      header: mmToPx(sizes.standard.headerMm),
      footer: mmToPx(sizes.standard.footerMm),
    },
  };
};

const calcLandscape = (formatOption) => {
  const width = mmToPx(formatOption.heightMm);
  const height = mmToPx(formatOption.widthMm);
  return {
    width,
    height,
    headersSize: formatHeadersSize(formatOption.landscape),
  };
};

const calcPortrait = (formatOption) => {
  const width = mmToPx(formatOption.widthMm);
  const height = mmToPx(formatOption.heightMm);
  return {
    width,
    height,
    headersSize: formatHeadersSize(formatOption.portrait),
  };
};

const checkPageSize = ({ format, orientation }) => {
  const formatOption = PDF_FORMAT_OPTIONS.find((opt) => opt.id === format);

  if (orientation === 'landscape') {
    return calcLandscape(formatOption);
  }

  return calcPortrait(formatOption);
};

const isValidValue = (value) => {
  return value !== null && value !== undefined && value !== '';
};

const checkPlaceholderSources = (query) => {
  return getListOfNodes(query)
    .filter((node) => isNodePlaceholder(node))
    .some(({ data }) => !isValidValue(data?.props?.placeholder));
};

const checkTableSources = (query) => {
  return getListOfNodes(query)
    .filter((node) => isNodeTable(node))
    .some(({ data }) => !isValidValue(data?.props?.listSrc));
};

const checkCardsSources = (query) => {
  return getListOfNodes(query)
    .filter((node) => isNodeCards(node))
    .some(({ data }) => !isValidValue(data?.props?.cardSrc));
};

const checkTableAndGridRows = (query) => {
  return getListOfNodes(query).filter((node) => {
    return (
      (isNodeEmailContainerRow(node) && !isNodeValid(node)) ||
      (isNodeTable(node) && !isNodeValid(node))
    );
  });
};

const validateDomForSave = (query) => {
  const invalidRows = checkTableAndGridRows(query);
  const invalidTableSource = checkTableSources(query);
  const invalidCardsSource = checkCardsSources(query);
  const invalidPlaceholderSource = checkPlaceholderSources(query);

  return {
    invalidRows,
    invalidTableSource,
    invalidCardsSource,
    invalidPlaceholderSource,
  };
};

const getErrorMessages = ({
  invalidRows,
  invalidTableSource,
  invalidCardsSource,
  invalidPlaceholderSource,
}) => {
  const errors = [];

  if (invalidTableSource) errors.push('missing_table_src');
  if (invalidRows?.length) errors.push('invalid_rows');
  if (invalidCardsSource) errors.push('missing_cards_src');
  if (invalidPlaceholderSource) errors.push('invalid_placeholder_src');

  return errors;
};

const mapErrorMessages = ({ errorMessageKeys, t }) => {
  const errorMessages = errorMessageKeys.reduce(
    (aggMessage, currentConstant) => {
      aggMessage = `${aggMessage} ${t(currentConstant)};`;
      return aggMessage;
    },
    '',
  );

  return errorMessages;
};

const mapFailedEmails = (failedEmails) => (prevState) => {
  const newState = copyObjectWithoutRef(prevState);
  return newState.map((recipient) => {
    const recipientWithFailedEmail = failedEmails.find(
      ({ id }) => id === recipient.id,
    );

    if (recipientWithFailedEmail) {
      recipient.invalidEmail = recipientWithFailedEmail;
    }
    return recipient;
  });
};

const mapTranslatedOptions = (t) => () => {
  return {
    templateType: TEMPLATE_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    })),
    pdfOrientation: PDF_ORIENTATION_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    })),
  };
};

const upperFirstLetter = (str) => {
  return (capitalized = str[0].toUpperCase() + str.slice(1));
};

const mapDeleteResponse = ({ data, templates }) => {
  return data.map((dataTemplate) => {
    const foundTemplate = templates.find(
      ({ id }) => id === dataTemplate?.templateId,
    );
    return {
      ...dataTemplate,
      id: foundTemplate?.id || dataTemplate?.templateId,
      name: foundTemplate?.name || '',
    };
  });
};

const isTemplateDeletionAllowed = (deleteType) => {
  return deleteType !== TEMPLATE_DELETION_STATUSES.NONE;
};

export {
  upperFirstLetter,
  mapTranslatedOptions,
  mapFailedEmails,
  isValidValue,
  validateDomForSave,
  getErrorMessages,
  mapErrorMessages,
  mmToIn,
  mmToPx,
  mmToPt,
  filterEmptyStrings,
  parseEachOnTbodyRecursive,
  getLocalStorage,
  setLocalStorage,
  getSessionStorage,
  setSessionStorage,
  clearLocalStorage,
  copyObjectWithoutRef,
  findStringIndex,
  calcNodesWidth,
  updateFavicon,
  checkChangedFields,
  getTemplateSource,
  readDownload,
  removeEditableFlags,
  isValidEmail,
  isResponseOk,
  isResponseBarRequest,
  isNodeEmailContainerRow,
  isNodeEmailContainerColumn,
  isNodeValid,
  isNodeTable,
  isNodePlaceholder,
  isNodeTableRow,
  isNodeTableCell,
  findNodeByParentElement,
  findNodeById,
  getNodeSrc,
  findTableElementByParentId,
  findPathRecursive,
  normalizeUrl,
  urlRegex,
  isObject,
  isString,
  createHtmlStringWithConvertedDom,
  isCreateOk,
  isDetails,
  isEdit,
  isCreate,
  isTemplateTypeEmail,
  isTemplateTypePdfStyled,
  isTemplateTypePdfProgrammatic,
  formatHeadersSize,
  calcLandscape,
  calcPortrait,
  checkPageSize,
  isTemplatePdf,
  isNodeCards,
  getParentSrc,
  getListOfNodes,
  filterNodesByParentId,
  removeHandlebarsEach,
  mapDeleteResponse,
  isTemplateDeletionAllowed,
  getTemplateSource2,
};
